import ReactPaginate from 'react-paginate';
import React from 'react';
import { useTranslation } from 'react-i18next';

/*
## Props object for PaginationMC

onPageChange: required
  - Function to be called when the page changes. It receives the selected page as a parameter.

currentPage: required
  - Number that defines the current page.
  - Default value: 1

totalPages: required
  - Number that defines the total number of pages.

variant: optional
  - String that defines the style of the pagination component.
  - Default value: 'minimalJustifyCenter'
  - Possible values: 'boxedAlignLeft', 'boxedCenterFlex', 'minimalJustifyCenter', 'minimalAlignLeft'.

nextLabel: optional
  - String that defines the label for the next button.
  - Default value: 'Siguiente'

previousLabel: optional
  - String that defines the label for the previous button.
  - Default value: 'Anterior'

pageRangeDisplayed: optional
  - Number that defines the number of pages to be displayed in the pagination component.
  - Default value: 5

marginPagesDisplayed: optional
  - Number that defines the number of pages to be displayed at the beginning and end of the pagination component.
  - Default value: 2

breakLabel: optional
  - String that defines the label for the break element.
  - Default value: '...'

renderOnZeroPageCount: optional
  - Function to be called when the total number of pages is 0.
  - Default value: null

*/

const VARIANT_STYLES = {
  boxedAlignLeft: {
    containerClassName: 'tw-flex tw-items-center tw-gap-0 tw-my-4',
    pageClassName: 'tw-inline-block',
    previousClassName: 'tw-inline-block',
    nextClassName: 'tw-inline-block',
    breakClassName: 'tw-inline-block',
    activeClassName: 'tw-inline-block ',
    pageLinkClassName:
      'tw-px-3 tw-py-3 tw-text-blue-500 border tw-border-gray-300 hover:tw-bg-gray-100',
    previousLinkClassName:
      'tw-px-3 tw-py-3 tw-text-blue-500 border tw-border-gray-300  hover:tw-bg-gray-100',
    nextLinkClassName:
      'tw-px-3 tw-py-3 tw-text-blue-500 border tw-border-gray-300  hover:tw-bg-gray-100',
    breakLinkClassName:
      'tw-px-3 tw-py-3 tw-text-gray-500 border tw-border-gray-300 hover:tw-bg-gray-100',
    activeLinkClassName:
      'tw-bg-blue-600 tw-text-white border !tw-border-gray-300 hover:tw-bg-blue-600'
  },
  boxedCenterFlex: {
    containerClassName: 'tw-flex tw-items-center tw-justify-center tw-gap-0 tw-my-4',
    pageClassName: 'tw-inline-block',
    previousClassName: 'tw-inline-block',
    nextClassName: 'tw-inline-block',
    breakClassName: 'tw-inline-block',
    activeClassName: 'tw-inline-block ',
    pageLinkClassName:
      'tw-px-3 tw-py-3 tw-text-blue-500 border tw-border-gray-300 hover:tw-bg-gray-100',
    previousLinkClassName:
      'tw-px-3 tw-py-3 tw-text-blue-500 border tw-border-gray-300  hover:tw-bg-gray-100',
    nextLinkClassName:
      'tw-px-3 tw-py-3 tw-text-blue-500 border tw-border-gray-300  hover:tw-bg-gray-100',
    breakLinkClassName:
      'tw-px-3 tw-py-3 tw-text-gray-500 border tw-border-gray-300 hover:tw-bg-gray-100',
    activeLinkClassName:
      'tw-bg-blue-600 tw-text-white border !tw-border-gray-300 hover:tw-bg-blue-600'
  },
  minimalJustifyCenter: {
    containerClassName: ' tw-flex tw-items-center tw-justify-center tw-list-none tw-my-4',
    pageClassName: 'tw-px-3 tw-py-2',
    previousClassName: 'tw-px-3 tw-py-2',
    nextClassName: 'tw-px-3 tw-py-2',
    breakClassName: 'tw-px-3 tw-py-2',
    activeClassName: 'tw-px-3 tw-py-2',
    pageLinkClassName: 'tw-text-blue-500',
    previousLinkClassName: 'tw-text-blue-500 ',
    nextLinkClassName: 'tw-text-blue-500 ',
    activeLinkClassName: '!tw-text-black'
  },
  minimalAlignLeft: {
    containerClassName: ' tw-flex tw-items-center tw-list-none tw-my-4',
    pageClassName: 'tw-px-3 tw-py-2',
    previousClassName: 'tw-px-3 tw-py-2',
    nextClassName: 'tw-px-3 tw-py-2',
    breakClassName: 'tw-px-3 tw-py-2',
    activeClassName: 'tw-px-3 tw-py-2',
    pageLinkClassName: 'tw-text-blue-500',
    previousLinkClassName: 'tw-text-blue-500 ',
    nextLinkClassName: 'tw-text-blue-500 ',
    activeLinkClassName: '!tw-text-black'
  }
};

const PaginationMC = (props) => {
  const { t } = useTranslation();
  const {
    onPageChange,
    totalPages,
    currentPage,
    variant = 'minimalJustifyCenter',
    nextLabel = t('paginate.next'),
    previousLabel = t('paginate.previous'),
    pageRangeDisplayed = 5,
    marginPagesDisplayed = 2,
    breakLabel = '...',
    renderOnZeroPageCount = null
  } = props;

  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.alignLeft;

  const previousClassName = `${styles.previousClassName || ''} ${
    currentPage === 1 ? 'tw-hidden' : ''
  }`.trim();
  const nextClassName = `${styles.nextClassName || ''} ${
    currentPage === totalPages ? 'tw-hidden' : ''
  }`.trim();

  return (
    <div className="tw-wrapper">
      <ReactPaginate
        onPageChange={onPageChange}
        nextLabel={nextLabel}
        previousLabel={previousLabel}
        pageRangeDisplayed={pageRangeDisplayed}
        marginPagesDisplayed={marginPagesDisplayed}
        pageCount={totalPages}
        breakLabel={breakLabel}
        renderOnZeroPageCount={renderOnZeroPageCount}
        forcePage={currentPage - 1}
        previousClassName={previousClassName}
        nextClassName={nextClassName}
        containerClassName={styles.containerClassName}
        pageClassName={styles.pageClassName}
        breakClassName={styles.breakClassName}
        pageLinkClassName={styles.pageLinkClassName}
        previousLinkClassName={styles.previousLinkClassName}
        nextLinkClassName={styles.nextLinkClassName}
        breakLinkClassName={styles.breakLinkClassName}
        activeClassName={styles.activeClassName}
        activeLinkClassName={styles.activeLinkClassName}
      />
    </div>
  );
};

export default PaginationMC;
